import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Numbers as NumbersIcon,
  MedicalServices as MedicalServicesIcon,
  LocationOn as LocationOnIcon,
  Science as ScienceIcon,
  NoteAdd as NoteAddIcon
} from '@mui/icons-material';

import Cases from './personal/Cases';
import Modalities from './personal/Modalities';
import Regions from './personal/Regions';
import Studies from './personal/Studies';
import Addenda from './personal/Addenda';

const PersonalStatistics = forwardRef(({ currentUser }, ref) => {
  const { t } = useTranslation();
  
  // State
  const [activeCategory, setActiveCategory] = useState('fallzahlen');
  
  // Refs für Komponenten
  const casesRef = useRef(null);
  const modalitiesRef = useRef(null);
  const regionsRef = useRef(null);
  const studiesRef = useRef(null);
  const addendaRef = useRef(null);

  // Categories
  const categories = [
    { id: 'fallzahlen', label: t('statistics.personal.categories.fallzahlen', 'Fallzahlen'), icon: <NumbersIcon /> },
    { id: 'modalitaeten', label: t('statistics.personal.categories.modalitaeten', 'Modalitäten'), icon: <MedicalServicesIcon /> },
    { id: 'regionen', label: t('statistics.personal.categories.regionen', 'Regionen'), icon: <LocationOnIcon /> },
    { id: 'studienart', label: t('statistics.personal.categories.studienart', 'Studienart'), icon: <ScienceIcon /> },
    { id: 'addenda', label: t('statistics.personal.categories.addenda', 'Addenda'), icon: <NoteAddIcon /> }
  ];

  // Expose download functions to parent component via ref
  useImperativeHandle(ref, () => ({
    handlePdfDownload: () => {
      if (activeCategory === 'fallzahlen' && casesRef.current) {
        casesRef.current.handlePdfDownload();
      } else if (activeCategory === 'modalitaeten' && modalitiesRef.current) {
        modalitiesRef.current.handlePdfDownload();
      } else if (activeCategory === 'regionen' && regionsRef.current) {
        regionsRef.current.handlePdfDownload();
      } else if (activeCategory === 'studienart' && studiesRef.current) {
        studiesRef.current.handlePdfDownload();
      } else if (activeCategory === 'addenda' && addendaRef.current) {
        addendaRef.current.handlePdfDownload();
      }
    },
    handleCsvDownload: () => {
      if (activeCategory === 'fallzahlen' && casesRef.current) {
        casesRef.current.handleCsvDownload();
      } else if (activeCategory === 'modalitaeten' && modalitiesRef.current) {
        modalitiesRef.current.handleCsvDownload();
      } else if (activeCategory === 'regionen' && regionsRef.current) {
        regionsRef.current.handleCsvDownload();
      } else if (activeCategory === 'studienart' && studiesRef.current) {
        studiesRef.current.handleCsvDownload();
      } else if (activeCategory === 'addenda' && addendaRef.current) {
        addendaRef.current.handleCsvDownload();
      }
    }
  }));

  const renderContent = () => {
    switch (activeCategory) {
      case 'fallzahlen':
        return <Cases ref={casesRef} currentUser={currentUser} />;
      
      case 'modalitaeten':
        return <Modalities ref={modalitiesRef} currentUser={currentUser} />;
      
      case 'regionen':
        return <Regions ref={regionsRef} currentUser={currentUser} />;
      
      case 'studienart':
        return <Studies ref={studiesRef} currentUser={currentUser} />;
      
      case 'addenda':
        return <Addenda ref={addendaRef} currentUser={currentUser} />;
      
      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Sidebar mit Kategorien */}
      <Paper sx={{ width: 280, mr: 3, flexShrink: 0, overflow: 'auto' }}>
        <List>
          {categories.map((category) => (
            <ListItem key={category.id} disablePadding>
              <ListItemButton
                selected={activeCategory === category.id}
                onClick={() => setActiveCategory(category.id)}
              >
                <ListItemIcon>
                  {category.icon}
                </ListItemIcon>
                <ListItemText primary={category.label} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Hauptinhalt */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {renderContent()}
      </Box>
    </Box>
  );
});

PersonalStatistics.displayName = 'PersonalStatistics';

export default PersonalStatistics;